import React, { useCallback, useState, useContext } from "react";
import { useNotify } from "react-admin";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Box from "@mui/material/Box";

import { createComment, createOrderItem } from "./sharedMethods";
import { useProfile } from "../DataHooks/useProfile";
import {
  store,
  ADD_ITEM_DESC,
  ADD_SPEC_DESC,
  SET_UNIT_PRICE,
  SET_LOADING,
  RESET,
} from "./store";
import { getCookie } from "../../helpers/Cookies";

const Root = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(2),
}));

const ControlPanel = () => {
  const { dispatch, state } = useContext(store);
  const { itemDesc, specDesc, unitPrice, video, orders } = state;
  const { profile } = useProfile();

  const notify = useNotify();
  const token = getCookie("token");
  const [startLoading, setStartLoading] = useState(false);
  const [endLoading, setEndLoading] = useState(false);

  const onChangeItem = useCallback(
    (e) => dispatch({ type: ADD_ITEM_DESC, payload: e.target.value }),
    [dispatch]
  );
  const onChangeSpec = useCallback(
    (e) => dispatch({ type: ADD_SPEC_DESC, payload: e.target.value }),
    [dispatch]
  );
  const onChangeUnitPrice = useCallback(
    (e) => {
      dispatch({ type: SET_UNIT_PRICE, payload: e.target.value });
    },
    [dispatch]
  );

  const postNameList = useCallback(() => {
    const nameList = orders
      .map((o) => `${o.customerName}+${o.quantity}`)
      .join(", ");
    const value = `${itemDesc}\n${nameList}`;
    createComment({ value, video, profile })
      .then(() => {
        notify("Name list posted");
      })
      .catch(() => {
        notify("Post name list failed", { type: "warning" });
      });
  }, [video, profile, orders, itemDesc, notify]);

  const postStartComment = useCallback(() => {
    setStartLoading(true);
    const value = `---START---\n${itemDesc}\nPrice ${unitPrice}\n${specDesc}`;
    createComment({ value, video, profile })
      .then(() => {
        notify("Successful");
        setStartLoading(false);
      })
      .catch(() => {
        notify("Error", { type: "warning" });
        setStartLoading(false);
      });
  }, [notify, itemDesc, unitPrice, specDesc, video, profile]);

  const postEndComment = useCallback(() => {
    setEndLoading(true);
    const value = `---END---\n${itemDesc}\n`;
    createComment({ value, video, profile })
      .then(() => {
        notify("Successful");
        setEndLoading(false);
      })
      .catch(() => {
        notify("Error", { type: "warning" });
        setEndLoading(false);
      });
  }, [video, profile, notify, itemDesc]);

  const submitOrders = useCallback(() => {
    dispatch({ type: SET_LOADING, payload: true });
    const newOrders = orders.map((o) => ({
      ...o,
      description: `${o.description} x${o.quantity}`,
    }));
    const payload = {
      data: { orders: newOrders },
    };
    createOrderItem({ token, payload })
      .then(() => {
        notify("Orders submitted successfully");
        postNameList();
        dispatch({ type: RESET });
        dispatch({ type: SET_LOADING, payload: false });
      })
      .catch(() => {
        notify("Error: submit order failed", { type: "warning" });
        dispatch({ type: SET_LOADING, payload: false });
      });
  }, [dispatch, token, orders, notify, postNameList]);

  return (
    <Root>
      <Grid container spacing={2}>
        {/* Item + Unit Price (stacked) */}
        <Grid size={{ xs: 12, sm: 4 }} container direction="column" spacing={2}>
          <Grid size={12}>
            <TextField
              label="Item"
              required
              fullWidth
              onChange={onChangeItem}
              value={itemDesc}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Unit Price"
              type="number"
              fullWidth
              placeholder="e.g. 22"
              onChange={onChangeUnitPrice}
              value={unitPrice}
            />
          </Grid>
        </Grid>

        {/* Specifications */}
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            label="Specifications"
            multiline
            rows={4}
            fullWidth
            placeholder="a*1, b*2"
            variant="outlined"
            onChange={onChangeSpec}
            value={specDesc}
          />
        </Grid>

        {/* Buttons */}
        <Grid size={12}>
          <ButtonGroup color="primary" aria-label="control panel actions">
            <Button
              onClick={postStartComment}
              disabled={!itemDesc || startLoading}
            >
              Start
            </Button>
            <Button onClick={postEndComment} disabled={!itemDesc || endLoading}>
              End
            </Button>
            <Button disabled={orders.length === 0} onClick={submitOrders}>
              Submit
            </Button>
          </ButtonGroup>
        </Grid>
      </Grid>
    </Root>
  );
};

export default ControlPanel;
